import { StreamingPlayerImage, StreamingPlayerPageLayoutPosition, StreamingPlayerTypography } from '@heardis/stream-contracts';
import classes from './Header.module.css';
import useMediaQuery from '../../../hooks/useMediaQuery';
import InfoIcon from '../../../elements/icons/InfoIcon';
import { classNames } from '../../../utils/styles';
import { BackgroundImage, Image } from '../../Image';

type HeaderProps = {
  backgroundColor: string;
  backgroundImage: StreamingPlayerImage;
  typography: StreamingPlayerTypography;
  logo: {
    image: StreamingPlayerImage;
    position: StreamingPlayerPageLayoutPosition;
    size: string;
  };
  title: {
    content: string;
    typography: StreamingPlayerTypography;
    position: StreamingPlayerPageLayoutPosition;
  };
  descriptionEnabled: boolean;
  showDescription: () => void;
};

function Header({ backgroundColor, backgroundImage, title, logo, descriptionEnabled, showDescription, typography }: HeaderProps): JSX.Element {
  const isMobile = !useMediaQuery('(min-width: 1024px)');
  const { fontFamily, fontSize, fontWeight, color } = typography;
  const { content: titleContent, typography: titleTypography, position: titlePosition } = title;
  const { image, position: logoPosition, size: logoSize } = logo;
  const withLogo = image.url && (logoPosition !== StreamingPlayerPageLayoutPosition.HIDDEN);
  const withTitle = titleContent && (titlePosition !== StreamingPlayerPageLayoutPosition.HIDDEN);
  const positions = [logoPosition, titlePosition];
  const allHidden = positions.every((position) => position === StreamingPlayerPageLayoutPosition.HIDDEN);
  const positionsWithoutHidden = positions.filter((position) => position !== StreamingPlayerPageLayoutPosition.HIDDEN);
  const allEnd = !allHidden && positionsWithoutHidden.every((position) => position === StreamingPlayerPageLayoutPosition.END);
  const allStart = !allHidden && positionsWithoutHidden.every((position) => position === StreamingPlayerPageLayoutPosition.START);
  const allCentered = !allHidden && positionsWithoutHidden.every((position) => position === StreamingPlayerPageLayoutPosition.CENTER);

  return (
    <header
      className={classes.header}
      style={{
        backgroundColor,
        paddingBottom: isMobile && descriptionEnabled ? 'var(--spacing-xl)' : undefined,
        fontFamily,
        fontSize,
        fontWeight,
        color,
      }}
    >
      {allEnd || allCentered || allHidden ? null : (
        <div className={classNames(classes.headerArea, classes.headerAreaLeft)}>
          {withLogo && logoPosition === StreamingPlayerPageLayoutPosition.START && (
            <Image url={image.url} alt={image.alt} blur={image.blur} position={image.position} fit={image.fit} height={logoSize} />
          )}
          {withTitle && titlePosition === StreamingPlayerPageLayoutPosition.START && (
          <h1
            className={classes.title}
            style={{
              textAlign: titlePosition,
              fontFamily: titleTypography.fontFamily,
              fontSize: titleTypography.fontSize,
              fontWeight: titleTypography.fontWeight,
              color: titleTypography.color,
            }}
          >
            {titleContent}
          </h1>
          )}
        </div>
      )}
      {allHidden ? null : (
        <div className={classNames(classes.headerArea, classes.headerAreaCenter)}>
          {withLogo && logoPosition === StreamingPlayerPageLayoutPosition.CENTER && (
          <Image url={image.url} alt={image.alt} blur={image.blur} position={image.position} fit={image.fit} height={logoSize} />
          )}
          {withTitle && titlePosition === StreamingPlayerPageLayoutPosition.CENTER && (
          <h1
            className={classes.title}
            style={{
              textAlign: titlePosition,
              fontFamily: titleTypography.fontFamily,
              fontSize: titleTypography.fontSize,
              fontWeight: titleTypography.fontWeight,
              color: titleTypography.color,
            }}
          >
            {titleContent}
          </h1>
          )}
        </div>
      )}
      {allStart || allCentered || allHidden ? null : (
        <div className={classNames(classes.headerArea, classes.headerAreaRight)}>
          {withLogo && logoPosition === StreamingPlayerPageLayoutPosition.END && (
          <Image url={image.url} alt={image.alt} blur={image.blur} position={image.position} fit={image.fit} height={logoSize} />
          )}
          {withTitle && titlePosition === StreamingPlayerPageLayoutPosition.END && (
          <h1
            className={classes.title}
            style={{
              textAlign: titlePosition,
              fontFamily: titleTypography.fontFamily,
              fontSize: titleTypography.fontSize,
              fontWeight: titleTypography.fontWeight,
              color: titleTypography.color,
            }}
          >
            {titleContent}
          </h1>
          )}
        </div>
      )}
      {isMobile && descriptionEnabled && (
      <button type="button" className={classes.info} style={{ color: titleTypography.color }} onClick={showDescription}>
        <InfoIcon />
        more info
      </button>
      )}
      {backgroundImage.url && (
        <BackgroundImage
          url={backgroundImage.url}
          alt={backgroundImage.alt}
          blur={backgroundImage.blur}
          position={backgroundImage.position}
          fit={backgroundImage.fit}
          zIndex={-5}
        />
      )}
    </header>
  );
}

export default Header;
